import { useCallback, useEffect, useState } from "react";
import { getMySubscriptions, cancelSubscription } from "../api/checkout";
import Badge from "./ds/Badge";
import Button from "./ds/Button";

/**
 * The member's own subscriptions, with the one action they actually need: cancel.
 *
 * Cancelling never takes access away immediately — the period is already paid for, so
 * it runs to `currentPeriodEnd`. Saying that up front is what stops people hesitating
 * over the button.
 *
 * @param {{ isDark: boolean, setToast?: (m: string) => void }} props
 */
const SubscriptionsPanel = ({ setToast }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getMySubscriptions()
      .then((res) => setRows(res?.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleCancel = async (id) => {
    setBusyId(id);
    try {
      const res = await cancelSubscription(id);
      setToast?.(
        res?.accessUntil
          ? `Cancelled. You keep access until ${new Date(res.accessUntil).toLocaleDateString()}.`
          : "Subscription cancelled."
      );
      setConfirmId(null);
      load();
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not cancel the subscription.");
    } finally {
      setBusyId(null);
    }
  };

  // Nothing to manage — don't take up space on the page.
  if (!loading && rows.length === 0) return null;

  const statusTone = (status) => {
    if (status === "active" || status === "trialing") return "success";
    if (status === "past_due") return "warning";
    return "neutral";
  };
  const statusLabel = (s) =>
    ({
      trialing: "Free trial",
      active: "Active",
      past_due: "Payment failed",
      canceled: "Cancelled",
      revoked: "Ended",
      pending: "Starting…",
    })[s] || s;

  const cadence = (row) =>
    row.intervalCount > 1
      ? `every ${row.intervalCount} ${row.interval === "WEEKLY" ? "weeks" : "months"}`
      : row.interval === "WEEKLY"
        ? "weekly"
        : "monthly";

  return (
    <section className="flex flex-col gap-space-12">
      <h2 className="text-title-3 text-label-primary">Subscriptions</h2>

      {loading ? (
        <p className="text-subheadline text-label-secondary">Loading…</p>
      ) : (
        <ul className="flex flex-col rounded-radius-lg bg-bg-raised p-space-8">
          {rows.map((row) => {
            const live = ["active", "trialing", "past_due"].includes(row.status);
            const renews = row.currentPeriodEnd ? new Date(row.currentPeriodEnd).toLocaleDateString() : null;

            return (
              <li key={row._id} className="flex flex-col gap-space-8 rounded-[20px] p-space-8">
                <div className="flex items-start justify-between gap-space-16">
                  <div className="flex min-w-0 flex-col gap-space-4">
                    <p className="truncate text-subheadline-semibold text-label-primary">{row.mintSpace?.name || "Subscription"}</p>
                    <p className="text-footnote text-label-secondary">
                      {row.amount} {row.currency} · {cadence(row)}
                    </p>
                    {row.cardMask && (
                      <p className="text-footnote text-label-tertiary">
                        {row.cardBrand} {row.cardMask}
                      </p>
                    )}
                    {renews && (
                      <p className="text-footnote text-label-tertiary">
                        {live ? (row.status === "canceled" ? `Access until ${renews}` : `Renews ${renews}`) : `Ended ${renews}`}
                      </p>
                    )}
                    {row.status === "past_due" && row.lastChargeError && (
                      <p className="text-footnote text-status-warning">{row.lastChargeError}</p>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-space-8">
                    <Badge size="s" tone={statusTone(row.status)}>
                      {statusLabel(row.status)}
                    </Badge>
                    {live && confirmId !== row._id && (
                      <Button variant="plain" size="s" onClick={() => setConfirmId(row._id)}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>

                {confirmId === row._id && (
                  <div className="flex flex-col gap-space-12 rounded-radius-sm bg-bg-inset p-space-12">
                    <p className="text-footnote text-label-secondary">
                      You'll keep access
                      {renews ? ` until ${renews}` : " for the rest of this period"} — you just won't be charged again.
                    </p>
                    <div className="flex gap-space-8">
                      <Button variant="danger" size="s" loading={busyId === row._id} onClick={() => handleCancel(row._id)}>
                        {busyId === row._id ? "Cancelling…" : "Yes, cancel"}
                      </Button>
                      <Button variant="gray" size="s" onClick={() => setConfirmId(null)}>
                        Keep it
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default SubscriptionsPanel;
